import { MediaError, DOMException } from 'global';
import {
  // Messages
  ERR_ABORTED_MSG,
  ERR_SRC_NOT_SUPPORTED_MSG,
  ERR_DECODE_MSG,
  ERR_NETWORK_MSG,
  ERR_UNKNOWN_MSG,
  ERR_SRC_INVALID_MSG,
  ERR_NO_PLAYABLE_SOURCES_MSG,

  // Codes
  ERR_ABORTED,
  ERR_NETWORK,
  ERR_DECODE,
  ERR_SRC_NOT_SUPPORTED,
  ERR_SRC_INVALID,
  ERR_NO_PLAYABLE_SOURCES,
  ERR_PLAYLIST,
  ERR_UNKNOWN
} from '../constants';

const messages = {
  [ERR_ABORTED]: ERR_ABORTED_MSG,
  [ERR_NETWORK]: ERR_NETWORK_MSG,
  [ERR_DECODE]: ERR_DECODE_MSG,
  [ERR_SRC_NOT_SUPPORTED]: ERR_SRC_NOT_SUPPORTED_MSG,
  [ERR_SRC_INVALID]: ERR_SRC_INVALID_MSG,
  [ERR_UNKNOWN]: ERR_UNKNOWN_MSG,
  [ERR_NO_PLAYABLE_SOURCES]: ERR_NO_PLAYABLE_SOURCES_MSG
};

const localeMessageKeys = {
  [ERR_ABORTED]: 'aborted',
  [ERR_NETWORK]: 'network',
  [ERR_DECODE]: 'decode',
  [ERR_SRC_NOT_SUPPORTED]: 'notsupported',
  [ERR_SRC_INVALID]: 'invalid_source',
  [ERR_UNKNOWN]: 'unknown',
  [ERR_NO_PLAYABLE_SOURCES]: 'no_source'
};

const fatalErrors = [ERR_DECODE, ERR_SRC_INVALID, ERR_NO_PLAYABLE_SOURCES, ERR_PLAYLIST];

export default class PlayerError extends Error {
  constructor (code = ERR_UNKNOWN, message = null, ...params) {
    message = message || (messages[code] && messages[code]) || ERR_UNKNOWN_MSG;

    super(message, ...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PlayerError);
    }

    this.code = code;
    this.fatal = fatalErrors.indexOf(code) !== -1;
  }

  // @todo move to the ui layer
  getLocaleMessage (locale) {
    const key = localeMessageKeys[this.code];

    if (key && locale.error && locale.error[key]) {
      return locale.error[key];
    }

    return this.message;
  }

  /**
   * Create a player error from an HTML5 media error
   *  - MediaError.MEDIA_ERR_ABORTED: The fetching of the associated resource was aborted by the user's request.
   *  - MediaError.MEDIA_ERR_NETWORK: Some kind of network error occurred which prevented the media from being successfully fetched, despite having previously been available.
   *  - MediaError.MEDIA_ERR_DECODE: Despite having previously been determined to be usable, an error occurred while trying to decode the media resource, resulting in an error.
   *  - MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED: The associated resource or media provider object (such as a MediaStream) has been found to be unsuitable.
   * @param {MediaError} error An HTML5 media error
   * @return {PlayerError}
   */
  static fromMediaError (error) {
    switch (error.code) {
      case MediaError.MEDIA_ERR_ABORTED:
        return new PlayerError(ERR_ABORTED, error.message, error.fileName, error.lineNumber);
      case MediaError.MEDIA_ERR_DECODE:
        return new PlayerError(ERR_DECODE, error.message, error.fileName, error.lineNumber);
      case MediaError.MEDIA_ERR_NETWORK:
        return new PlayerError(ERR_NETWORK, error.message, error.fileName, error.lineNumber);
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      case DOMException.NOT_SUPPORTED_ERR:
        return new PlayerError(ERR_SRC_NOT_SUPPORTED, error.message, error.fileName, error.lineNumber);
      default:
        return new PlayerError(ERR_UNKNOWN, error.message, error.fileName, error.lineNumber);
    }
  }
}

PlayerError.ABORTED = ERR_ABORTED;
PlayerError.NETWORK = ERR_NETWORK;
PlayerError.DECODE = ERR_DECODE;
PlayerError.UNKNOWN = ERR_UNKNOWN;
PlayerError.SRC_NOT_SUPPORTED = ERR_SRC_NOT_SUPPORTED;
PlayerError.SRC_INVALID = ERR_SRC_INVALID;
PlayerError.NO_PLAYABLE_SOURCES = ERR_NO_PLAYABLE_SOURCES;
PlayerError.PLAYLIST = ERR_PLAYLIST;
